import React, { useState, useSyncExternalStore } from 'react';
import { Alert, Box, Button, Snackbar, Stack, Typography } from '@mui/material';
import { finishSourceRecovery, sourceRecoveries, subscribeSourceRecovery } from 'shared/api/sourceAuthRecovery';
import { useEventListener } from 'shared/hooks';
import SourceSignInDialog from './SourceSignInDialog';

/** Mounted once so a report with many failed visuals presents one account action. */
export default function ConnectionRecovery() {
    const issues = useSyncExternalStore(subscribeSourceRecovery, sourceRecoveries);
    const [opened, setOpened] = useState<string | null>(null);
    useEventListener<CustomEvent<string>>('unity-open-source-recovery', event => setOpened(event.detail));
    const issue = issues.find(item => item.key === opened) || issues[0];
    if (!issue) return null;
    const name = issue.databaseName || issue.serverName || 'Data source';
    const close = () => { finishSourceRecovery(issue.key, false); setOpened(null); };
    return <>
        <Snackbar open={opened !== issue.key} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ zIndex: theme => theme.zIndex.modal + 1 }}>
            <Alert severity={issue.state === 'unavailable' ? 'info' : 'warning'} onClose={close}
                sx={{ width: 440, maxWidth: 'calc(100vw - 32px)', boxShadow: 3, alignItems: 'flex-start' }}>
                <Stack spacing={0.75}>
                    <Typography variant="subtitle2">{issue.canReconnect ? 'Sign-in required' : issue.state === 'setup_required' ? 'Connection setup needs attention' : issue.state === 'access_denied' ? 'Source access denied' : 'Source temporarily unavailable'}</Typography>
                    <Typography variant="body2">{name}</Typography>
                    <Typography variant="body2">{issue.canReconnect ? 'Reconnect your account to resume loading data. Your report and edits stay in place.' : issue.message}</Typography>
                    <Box><Button size="small" variant="contained" disableElevation onClick={() => setOpened(issue.key)}>
                        {issue.canReconnect ? 'Reconnect' : 'Connection details'}
                    </Button><Button size="small" onClick={close}>{issue.canReconnect ? 'Not now' : 'Dismiss'}</Button></Box>
                    {issues.length > 1 && <Typography variant="caption">{issues.length - 1} other {issues.length === 2 ? 'account needs' : 'accounts need'} attention</Typography>}
                </Stack>
            </Alert>
        </Snackbar>
        {opened === issue.key && <SourceSignInDialog connection={{ id: issue.connectionId!, name, serverType: issue.serverType || '',
            authType: issue.authType, serverName: issue.serverName || '', databaseName: issue.databaseName || '' }}
            reportId={issue.reportId} onClose={close} onReconnected={() => { finishSourceRecovery(issue.key, true); setOpened(null); }} />}
    </>;
}
